import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { serializer } from './serializer';

// ============ GET RESTAURANT DETAIL ============
bus.on(constants.EVENTS.RESTAURANT.RESTAURANT_DETAILS_REQUESTED, async (payload) => {
  const requestId = 'restaurantDetail-' + Date.now();

  if (!payload?.id) {
    bus.emit(constants.EVENTS.RESTAURANT.RESTAURANT_DETAIL_ERROR, {
      message: 'Restaurant ID is required'
    });
    return;
  }

  bus.emit(constants.EVENTS.RESTAURANT.RESTAURANT_DETAIL_LOADING);

  try {
    const url = constants.ENDPOINTS.RESTAURANTS.DETAIL.replace('{id}', payload.id);

    bus.emit('network.request', { id: requestId, url, method: 'GET' });

    bus.on('network.response', (res) => {
      if (res.id === requestId) {
        const parsed = serializer.deserialize(res);
        if (parsed.success) {
          bus.emit(constants.EVENTS.RESTAURANT.RESTAURANT_DETAIL_LOADED, parsed.data);
        } else {
          bus.emit(constants.EVENTS.RESTAURANT.RESTAURANT_DETAIL_ERROR, parsed);
        }
      }
    });
  } catch (error) {
    bus.emit(constants.EVENTS.RESTAURANT.RESTAURANT_DETAIL_ERROR, {
      message: error.message || 'Failed to fetch restaurant details'
    });
  }
});

// ============ GET RESTAURANT DISHES ============
bus.on(constants.EVENTS.RESTAURANT.DISHES_REQUESTED, async (payload) => {
  const requestId = 'restaurantDishes-' + Date.now();
  const storeId = payload?.storeId || payload?.id;

  if (!storeId) {
    bus.emit(constants.EVENTS.RESTAURANT.DISHES_ERROR, {
      message: 'Store ID is required for dishes'
    });
    return;
  }

  try {
    const url = constants.ENDPOINTS.RESTAURANTS.DISHES.replace('{storeId}', storeId);

    bus.emit('network.request', { id: requestId, url, method: 'GET' });

    bus.on('network.response', (res) => {
      if (res.id === requestId) {
        const parsed = serializer.deserializeDishes(res);
        if (parsed.success) {
          const dishes = parsed.data || [];
          bus.emit(constants.EVENTS.RESTAURANT.DISHES_LOADED, dishes);
        } else {
          bus.emit(constants.EVENTS.RESTAURANT.DISHES_ERROR, parsed);
        }
      }
    });
  } catch (error) {
    bus.emit(constants.EVENTS.RESTAURANT.DISHES_ERROR, {
      message: error.message || 'Failed to fetch restaurant dishes'
    });
  }
});

// ============ UPDATE RESTAURANT STATS ============
bus.on(constants.EVENTS.RESTAURANT.UPDATE_STATS_REQUESTED, async (payload) => {
  const { restaurantId, action, value } = payload;
  if (!restaurantId || !action) return;

  const requestId = 'restStat-' + Date.now();
  const url = constants.ENDPOINTS.RESTAURANTS.STATS.replace('{id}', restaurantId);

  bus.emit('network.request', { 
    id: requestId, 
    url, 
    method: 'POST',
    data: { action, value: value || 1 }
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off('network.response', handler);
      if (res.success) {
        bus.emit(constants.EVENTS.RESTAURANT.UPDATE_STATS_SUCCESS, res.data);
      } else {
        bus.emit(constants.EVENTS.RESTAURANT.UPDATE_STATS_ERROR, res);
      }
    }
  };
  bus.on('network.response', handler);
});

// ============ RATE RESTAURANT ============
bus.on(constants.EVENTS.RESTAURANT.RATE, async (payload) => {
  const { restaurantId, rating } = payload;
  if (!restaurantId || !rating) return;

  const requestId = 'restRate-' + Date.now();
  const url = constants.ENDPOINTS.RESTAURANTS.STATS.replace('{id}', restaurantId);

  bus.emit('network.request', { 
    id: requestId, 
    url, 
    method: 'POST',
    data: { action: 'addRating', value: rating }
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off('network.response', handler);
      if (res.success) {
        bus.emit(constants.EVENTS.RESTAURANT.RATED, { restaurantId, rating, ...res.data });
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'success',
          title: 'Merci !',
          body: 'Votre avis a été pris en compte.'
        });
      } else {
        bus.emit(constants.EVENTS.RESTAURANT.RATE_ERROR, res);
      }
    }
  };
  bus.on('network.response', handler);
});


function enrichRestaurant(restaurant) {
  if (!restaurant || typeof restaurant !== 'object') return restaurant;

  // Calculer isWithinOpeningHours via le serializer
  const isWithinOpeningHours = serializer.isWithinOpeningHours(
    restaurant.openingHours
  );
  const isManualOpen =
    restaurant.isOpen !== undefined ? restaurant.isOpen !== false : true;

  return {
    ...restaurant,
    isWithinOpeningHours,
    isOpen: isManualOpen && isWithinOpeningHours
  };
}

function enrichRestaurantData(data) {
  if (!data) return data;

  if (Array.isArray(data)) {
    return data.map(enrichRestaurant);
  }
  if (Array.isArray(data?.data?.data)) {
    data.data.data = data.data.data.map(enrichRestaurant);
    return data;
  }
  if (Array.isArray(data?.data)) {
    data.data = data.data.map(enrichRestaurant);
    return data;
  }
  if (data?.data && typeof data.data === 'object') {
    data.data = enrichRestaurant(data.data);
    return data;
  }
  if (typeof data === 'object') {
    return enrichRestaurant(data);
  }
  return data;
}

function catchRestaurantRequest() {
  const pendingRestaurantRequests = new Map();

  // Intercepter toutes les requêtes réseau vers https://api.monrapidoo.com/restaurant/...
  bus.on(constants.EVENTS.NETWORK.REQUEST, (payload) => {
    if (payload?.id && payload?.url) {
      const url = String(payload.url).toLowerCase();
      if (
        url.includes('api.monrapidoo.com/restaurant/') ||
        url.includes('/restaurant/')
      ) {
        pendingRestaurantRequests.set(payload.id, payload.url);
      }
    }
  });

  // Intercepter les réponses et appliquer isWithinOpeningHours
  bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res?.id && pendingRestaurantRequests.has(res.id)) {
      pendingRestaurantRequests.delete(res.id);
      if (res.success && res.data) {
        res.data = enrichRestaurantData(res.data);
      }
    }
  });
}

// Initialiser l'intercepteur
catchRestaurantRequest();